import type {
  ErrorLike,
  ObservableQuery,
  WatchQueryFetchPolicy
} from '@apollo/client';
import {
  type GetRecipeResultChangeSetForRepositoryQuery,
  isOrganizationChangesetAOrganizationRevision,
  isOrganizationChangesetAOrganizationSearch,
  isOrganizationRecipeRunAOrganizationRecipeRunCanceled,
  isOrganizationRecipeRunAOrganizationRecipeRunError,
  isOrganizationRecipeRunAOrganizationRecipeRunFinished,
  isOrganizationRecipeRunAOrganizationRecipeRunQueued,
  isOrganizationRecipeRunAOrganizationRecipeRunRunning,
  isOrganizationRecipeRunAOrganizationRecipeRunSyncing,
  isRepositoryChangesetARepositoryRecipeRunCanceled,
  isRepositoryChangesetARepositoryRecipeRunError,
  isRepositoryChangesetARepositoryRecipeRunFinished,
  isRepositoryChangesetARepositoryRecipeRunNoLst,
  isRepositoryChangesetARepositoryRevision,
  type OrganizationRecipeRunCanceled,
  type OrganizationRecipeRunError,
  type OrganizationRecipeRunFinished,
  type OrganizationRecipeRunQueued,
  type OrganizationRecipeRunRunning,
  type OrganizationRecipeRunSyncing,
  type OrganizationRevision,
  type OrganizationSearch,
  type RecipeResultsForRepositoryQuery,
  type RecipeRunDataTablesProducedQuery,
  type RecipeRunMetadataQuery,
  type RecipeRunNavigationQuery,
  type RecipeRunOrganizationRepositoriesQuery,
  type RecipeRunOrganizationsQuery,
  type RecipeRunResultsQuery,
  type RecipeRunSummaryQuery,
  type RecipeRunSummaryQueryVariables,
  type RecipeRunTotalsFragment,
  type RepositoryChangesetFieldsFragment,
  type RepositoryChangesetWhereInput
} from '../__generated__/apollo-hooks';
import { RESULTS_PAGE_SIZE, RESULTS_TREE_ORDER_BY } from '../constants/results';
import { isNotFoundError } from './graphql-error.helper';
import { getOrganizationDisplayName } from './organizations.helper';

// Alias generated type guards to shorter names; only the ones used outside
// this module are exported.
const isQueued = isOrganizationRecipeRunAOrganizationRecipeRunQueued;
export const isSyncing = isOrganizationRecipeRunAOrganizationRecipeRunSyncing;
export const isRunning = isOrganizationRecipeRunAOrganizationRecipeRunRunning;
export const isFinished = isOrganizationRecipeRunAOrganizationRecipeRunFinished;
export const isCanceled = isOrganizationRecipeRunAOrganizationRecipeRunCanceled;
export const isError = isOrganizationRecipeRunAOrganizationRecipeRunError;

/**
 * Union type representing all possible recipe run states.
 */
export type RecipeRunNode =
  | OrganizationRecipeRunQueued
  | OrganizationRecipeRunSyncing
  | OrganizationRecipeRunRunning
  | OrganizationRecipeRunFinished
  | OrganizationRecipeRunCanceled
  | OrganizationRecipeRunError;

/**
 * Type guard to filter out non-recipe-run changesets (revisions).
 * Returns true only for recipe run types.
 */
export const isRecipeRunNode = (node: unknown): node is RecipeRunNode => {
  return (
    isQueued(node) ||
    isSyncing(node) ||
    isRunning(node) ||
    isFinished(node) ||
    isCanceled(node) ||
    isError(node)
  );
};

/** A changeset a push created, with repositories and results but no recipe. */
export type RevisionNode = OrganizationRevision;
export const isRevisionNode = isOrganizationChangesetAOrganizationRevision;

/** A saved code search: the repositories it matched, with no recipe. */
export type SearchNode = OrganizationSearch;
export const isSearchNode = isOrganizationChangesetAOrganizationSearch;

/**
 * When a search's sweep ended. The queries select `finishedAt` as
 * `searchFinishedAt`; a node without it reads as not ended.
 */
export const getSearchFinishedAt = (node: unknown): Date | undefined => {
  if (!isSearchNode(node) || !('searchFinishedAt' in node)) {
    return undefined;
  }
  const { searchFinishedAt } = node;
  if (searchFinishedAt instanceof Date) {
    return searchFinishedAt;
  }
  return typeof searchFinishedAt === 'string'
    ? new Date(searchFinishedAt)
    : undefined;
};

export const isSearchRunning = (node: unknown): boolean =>
  isSearchNode(node) && getSearchFinishedAt(node) === undefined;

const getSearchState = (node: unknown): RecipeRunState | undefined => {
  if (!isSearchNode(node) || !('searchFinishedAt' in node)) {
    return undefined;
  }
  if (node.error) {
    return 'OrganizationRecipeRunError';
  }
  return isSearchRunning(node)
    ? 'OrganizationRecipeRunRunning'
    : 'OrganizationRecipeRunFinished';
};

/** A changeset with repositories and results but no recipe: a revision or a saved search. */
export type RecipelessNode = RevisionNode | SearchNode;
export const isRecipelessNode = (node: unknown): node is RecipelessNode =>
  isRevisionNode(node) || isSearchNode(node);

/** A revision, or a search whose sweep has ended: nothing more is coming. */
const isRecipelessSettled = (node: unknown): boolean =>
  isRecipelessNode(node) && !isSearchRunning(node);

/** What the results pages can show: a recipe run in any state, a revision or a search. */
export type ResultsNode = RecipeRunNode | RecipelessNode;

type TerminalTypename = NonNullable<
  | OrganizationRecipeRunFinished['__typename']
  | OrganizationRecipeRunCanceled['__typename']
  | OrganizationRecipeRunError['__typename']
>;

/**
 * Checks if the recipe run is in a terminal state (finished, canceled, or
 * errored). Narrows within the caller's own union rather than to the schema
 * types, so reading a field the document didn't select stays a compile error.
 */
export const isTerminal = <T extends { __typename?: string }>(
  node: T | null | undefined
): node is Extract<T, { __typename?: TerminalTypename }> => {
  return isFinished(node) || isCanceled(node) || isError(node);
};

/**
 * The two changeset levels, narrowed to their own `__typename` unions so the
 * arguments of `isRepositoryChangesetFinal` can't be transposed.
 */
type RepositoryChangesetRef = {
  __typename?: NonNullable<RepositoryChangesetFieldsFragment['__typename']>;
};
type OrganizationChangesetRef = {
  __typename?:
    | RecipeRunState
    | NonNullable<OrganizationRevision['__typename']>
    | NonNullable<OrganizationSearch['__typename']>;
};

/** A run until it ends, a search until its sweep ends; a revision has nothing to wait for. */
export const isChangesetSettled = (
  node: OrganizationChangesetRef | null | undefined
): boolean => isTerminal(node) || isRecipelessSettled(node);

/**
 * Checks if a single repository's own run-phase entry has resolved, unlike
 * `isTerminal` above, which checks the overall run.
 *
 * Duplicates `changeset-status.helpers.ts`'s `TERMINAL_RUN_PHASE_STATUSES` —
 * that one also weighs `syncStatus`, which this helper's caller doesn't
 * select. Keep both in sync if the union gains a new state.
 *
 * A permanent sync failure can leave a repository `RepositoryRecipeRunQueued`
 * forever, invisible to this check — prefer `isRepositoryChangesetFinal`,
 * which folds in that fallback.
 */
export const isRepositoryRunTerminal = (node: unknown): boolean => {
  return (
    isRepositoryChangesetARepositoryRecipeRunFinished(node) ||
    isRepositoryChangesetARepositoryRecipeRunCanceled(node) ||
    isRepositoryChangesetARepositoryRecipeRunError(node) ||
    isRepositoryChangesetARepositoryRecipeRunNoLst(node) ||
    isRepositoryChangesetARepositoryRevision(node)
  );
};

/**
 * Whether nothing more will change this repository's entry, so its results can
 * be acted on. The run-level fallbacks release a repository whose own entry
 * never resolves: a permanent sync failure strands one at `Queued` forever,
 * and a revision selects no repositories at all.
 *
 * Each condition is stated positively so a run state added to the schema
 * defaults to "not final" rather than silently releasing every repository.
 */
export const isRepositoryChangesetFinal = (
  repositoryChangeset: RepositoryChangesetRef | null | undefined,
  runNode: OrganizationChangesetRef | null | undefined
): boolean => {
  return (
    isRepositoryRunTerminal(repositoryChangeset) || isChangesetSettled(runNode)
  );
};

/**
 * Gets human-readable display text for a recipe run state.
 * Accepts unknown to handle GraphQL union types that include non-recipe-run changesets.
 */
export const getRecipeRunDisplayText = (node: unknown): string => {
  if (isQueued(node)) {
    return 'Queued';
  }
  if (isSyncing(node)) {
    return 'Syncing';
  }
  if (isRunning(node)) {
    return 'Running';
  }
  if (isFinished(node)) {
    return 'Finished';
  }
  if (isCanceled(node)) {
    return 'Canceled';
  }
  if (isError(node)) {
    return 'Error';
  }
  return 'Unknown';
};

/**
 * Uppercase state key for a recipe run, suitable for icon/badge lookups
 * (e.g. getIconAssetsByKey, getStatusBadgeColor). Derived from the generated
 * `__typename` union by stripping the `OrganizationRecipeRun` prefix and
 * uppercasing, so new run states flow through automatically:
 * `OrganizationRecipeRunFinished` → `FINISHED`.
 */
type RecipeRunStateKey = Uppercase<
  RecipeRunState extends `OrganizationRecipeRun${infer Suffix}` ? Suffix : never
>;

/**
 * Derives the uppercase state key from a recipe run node for icon/badge
 * lookups. Falls back to 'RUNNING' for unrecognized nodes.
 */
export const getRecipeRunStateKey = (
  node: RecipeRunNode | { __typename: RecipeRunState } | undefined
): RecipeRunStateKey => {
  if (isQueued(node)) {
    return 'QUEUED';
  }
  if (isSyncing(node)) {
    return 'SYNCING';
  }
  if (isRunning(node)) {
    return 'RUNNING';
  }
  if (isFinished(node)) {
    return 'FINISHED';
  }
  if (isCanceled(node)) {
    return 'CANCELED';
  }
  if (isError(node)) {
    return 'ERROR';
  }
  return 'RUNNING';
};

/**
 * Extracts the recipe run node from the nested query structure.
 * Queries return data in: data.organization.changesets.edges[0].node
 *
 * Uses function overloads to preserve specific return types for each query type.
 */
export function extractRecipeRunNode(
  data: RecipeRunSummaryQuery | undefined
):
  | RecipeRunSummaryQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunNavigationQuery | undefined
):
  | RecipeRunNavigationQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunMetadataQuery | undefined
):
  | RecipeRunMetadataQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: GetRecipeResultChangeSetForRepositoryQuery | undefined
):
  | GetRecipeResultChangeSetForRepositoryQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunResultsQuery | undefined
):
  | RecipeRunResultsQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunDataTablesProducedQuery | undefined
):
  | RecipeRunDataTablesProducedQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunOrganizationsQuery | undefined
):
  | RecipeRunOrganizationsQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeResultsForRepositoryQuery | undefined
):
  | RecipeResultsForRepositoryQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data: RecipeRunOrganizationRepositoriesQuery | undefined
):
  | RecipeRunOrganizationRepositoriesQuery['organization']['changesets']['edges'][number]['node']
  | undefined;
export function extractRecipeRunNode(
  data:
    | RecipeRunSummaryQuery
    | RecipeRunNavigationQuery
    | RecipeRunMetadataQuery
    | GetRecipeResultChangeSetForRepositoryQuery
    | RecipeRunResultsQuery
    | RecipeRunDataTablesProducedQuery
    | RecipeRunOrganizationRepositoriesQuery
    | RecipeRunOrganizationsQuery
    | RecipeResultsForRepositoryQuery
    | undefined
) {
  const edges = data?.organization?.changesets?.edges;
  if (!edges || edges.length === 0) {
    return undefined;
  }
  return (edges as Array<(typeof edges)[number]>).at(0)?.node;
}

/**
 * The single repository entry `getRecipeResultChangeSetForRepository` was
 * filtered down to, or null when the changeset has no entry for it yet.
 *
 * Read from every run state, not just terminal ones (#8590).
 */
export const extractRepositoryChangeset = (
  data: GetRecipeResultChangeSetForRepositoryQuery | undefined
) => extractRecipeRunNode(data)?.repositories.edges.at(0)?.node ?? null;

/** The results page's summary query: the tree's window, ordered like its per-organization pages. */
export const buildResultsTreeSummaryVariables = (
  organizationId: string,
  recipeRunId: string,
  repositoriesWhere: RepositoryChangesetWhereInput | undefined
): RecipeRunSummaryQueryVariables => ({
  organizationId,
  recipeRunId,
  repositoriesFirst: RESULTS_PAGE_SIZE,
  repositoriesWhere,
  repositoriesOrderBy: RESULTS_TREE_ORDER_BY
});

/**
 * Picks the initial fetch policy for `RecipeRunSummaryQuery` from whatever run
 * is already cached at mount. A terminal run is immutable, so on remount — e.g.
 * returning from the Data Tables tab, a separate route that unmounts this page —
 * we serve the cached window with `cache-first` rather than refetch. A network
 * refetch carries `repositoriesFirst` with no `after`, and relayStylePagination
 * drops existing edges when `after` is null, collapsing the accumulated
 * repositories connection back to a single page (#8504). Non-terminal (or
 * uncached) runs keep `cache-and-network` so their per-repo statuses stay fresh.
 */
export function initialRecipeRunSummaryFetchPolicy(
  cached: RecipeRunSummaryQuery | undefined
): WatchQueryFetchPolicy {
  return isTerminal(extractRecipeRunNode(cached))
    ? 'cache-first'
    : 'cache-and-network';
}

/**
 * Apollo `onQueryUpdated` handler for `RecipeRunSummaryQuery`. Refetches with
 * `repositoriesFirst` set to at least as many edges as are already cached
 * (floored at `minRepositoriesFirst`), preventing the relay connection from
 * collapsing on heartbeat / tab-visible / state-transition refetches.
 *
 * The floor differs per page: the repositories grid uses one page, the
 * split-view tree its larger scroll window (`constants/results.ts`).
 */
export function refetchRecipeRunSummaryPreservingSize(
  observableQuery: ObservableQuery,
  minRepositoriesFirst: number
) {
  const queryData = observableQuery.getCurrentResult().data as
    | RecipeRunSummaryQuery
    | undefined;
  const queryNode = extractRecipeRunNode(queryData);
  // `repositories` is non-null in the schema but can be absent on a
  // just-triggered run, so guard it as the tree/summary call sites do.
  const edgeCount = isRecipeRunNode(queryNode)
    ? (queryNode.repositories?.edges?.length ?? 0)
    : 0;
  // Fall back to the last requested window so a momentarily unreadable cache
  // (edgeCount === 0 while the connection has actually grown) can't shrink the
  // connection below what we previously asked for.
  const variables = observableQuery.variables as {
    repositoriesFirst?: number;
  };
  return observableQuery.refetch({
    ...observableQuery.variables,
    repositoriesFirst: Math.max(
      edgeCount,
      variables.repositoriesFirst ?? 0,
      minRepositoriesFirst
    )
  });
}

/**
 * Reads a query-aliased field (e.g. `canceledStartedAt`, `runningTotals`)
 * not declared on the shared `RecipeRunNode` type.
 */
const getAliasedField = <T>(node: object, alias: string): T | undefined =>
  alias in node ? (node as Record<string, T | undefined>)[alias] : undefined;

/**
 * Gets the start time from a recipe run node.
 * Returns queuedAt for queued state, startedAt for running/finished states.
 */
const getRecipeRunStartTime = (
  node: RecipeRunNode | undefined | null
): Date | undefined => {
  if (isQueued(node)) {
    return node.queuedAt;
  }
  if (isSyncing(node) || isRunning(node) || isFinished(node)) {
    return node.startedAt;
  }
  if (isCanceled(node)) {
    // startedAt is nullable on canceled runs (run may be canceled before starting).
    // Some queries alias it as canceledStartedAt to avoid type conflicts; fall back
    // to that alias when present.
    return (
      getAliasedField<Date | null>(node, 'canceledStartedAt') ??
      node.startedAt ??
      undefined
    );
  }
  if (isError(node)) {
    // Some queries alias startedAt → errorStartedAt on OrganizationRecipeRunError
    // (e.g. recipeRunSummary), so fall back to that when present.
    return (
      getAliasedField<Date | null>(node, 'errorStartedAt') ??
      node.startedAt ??
      undefined
    );
  }
  return undefined;
};

/**
 * Gets the end time from a recipe run node.
 * Only finished and canceled states have finishedAt.
 */
const getRecipeRunEndTime = (
  node: RecipeRunNode | undefined | null
): Date | undefined => {
  return isTerminal(node) ? node.finishedAt : undefined;
};

/**
 * Recipe run state type derived from the GraphQL union __typename.
 */
export type RecipeRunState = RecipeRunNode['__typename'];

/**
 * Return type for extractPageTitleData helper.
 */
export type PageTitleData = {
  organizationName?: string;
  recipeName?: string;
  repositoriesCount: number;
  repositoriesCompleted: number;
  syncPending: number;
  synced: number;
  syncFailed: number;
  syncCanceled: number;
  syncSkipped: number;
  state: RecipeRunState;
  errorMessage?: string | null;
  startedAt?: Date;
  finishedAt?: Date;
  timeSavings?: string;
};

export type QueryWithChangesets =
  | RecipeRunSummaryQuery
  | RecipeRunNavigationQuery
  | RecipeRunDataTablesProducedQuery;

export const isChangesetNotFound = (
  node: RecipeRunNode | RecipelessNode | undefined,
  data: QueryWithChangesets | undefined,
  error: ErrorLike | undefined
): boolean =>
  node === undefined &&
  // Query.organization is non-null, so a missing org arrives as a NOT_FOUND error with no data.
  (data !== undefined || isNotFoundError(error));

/**
 * Extract page title data from any query with the standard changesets structure.
 */
export const extractPageTitleData = (
  data: QueryWithChangesets | undefined
): PageTitleData => {
  const node = data?.organization?.changesets?.edges?.at(0)?.node;
  const recipeRun = node && 'repositories' in node ? node : undefined;
  const stateNode = node?.__typename
    ? { __typename: node.__typename }
    : undefined;
  const state: RecipeRunState =
    getSearchState(node) ??
    (isRecipeRunNode(stateNode)
      ? stateNode.__typename
      : 'OrganizationRecipeRunFinished');

  const repos =
    recipeRun && 'allRepositories' in recipeRun
      ? recipeRun.allRepositories
      : recipeRun?.repositories;

  const allRepos =
    recipeRun && 'allRepositories' in recipeRun
      ? recipeRun.allRepositories
      : undefined;

  const recipeRunNode = isRecipeRunNode(node) ? node : undefined;
  // A revision is titled by its name, and dated from when its first push made it.
  const revision =
    recipeRun && 'createdAt' in recipeRun ? recipeRun : undefined;

  return {
    organizationName: getOrganizationDisplayName(data?.organization),
    recipeName:
      (recipeRun && 'recipe' in recipeRun
        ? recipeRun.recipe?.instanceName || recipeRun.recipe?.displayName
        : revision?.name) || undefined,
    repositoriesCount: repos?.count ?? 0,
    repositoriesCompleted: repos?.completed ?? 0,
    syncPending:
      allRepos && 'syncPending' in allRepos
        ? (allRepos.syncPending as number)
        : 0,
    synced: allRepos && 'synced' in allRepos ? (allRepos.synced as number) : 0,
    syncFailed:
      allRepos && 'syncFailed' in allRepos
        ? (allRepos.syncFailed as number)
        : 0,
    syncCanceled:
      allRepos && 'syncCanceled' in allRepos
        ? ((allRepos as unknown as { syncCanceled: number }).syncCanceled ?? 0)
        : 0,
    syncSkipped:
      allRepos && 'syncSkipped' in allRepos
        ? ((allRepos as unknown as { syncSkipped: number }).syncSkipped ?? 0)
        : 0,
    state,
    errorMessage:
      node && isError(node) ? (node.errorMessage ?? undefined) : undefined,
    startedAt: getRecipeRunStartTime(recipeRunNode) ?? revision?.createdAt,
    finishedAt: getRecipeRunEndTime(recipeRunNode) ?? getSearchFinishedAt(node),
    // Populated incrementally while Running and finalized when Finished.
    timeSavings: getRecipeRunTotals(recipeRunNode)?.timeSavings ?? undefined
  };
};

/**
 * Gets recipe run totals for states that expose them: Running (populated
 * incrementally as repositories complete) and Finished (final counts).
 */
export const getRecipeRunTotals = (
  node: RecipeRunNode | undefined
): RecipeRunTotalsFragment | null | undefined => {
  // `totals` is aliased to `runningTotals` on Running (see results.graphql).
  if (isRunning(node)) {
    return getAliasedField<RecipeRunTotalsFragment | null>(
      node,
      'runningTotals'
    );
  }
  if (isFinished(node)) {
    // `totals` is declared on the Finished node, so it's always present. The
    // only difference from the returned RecipeRunTotalsFragment is
    // optional-vs-required __typename (present at runtime via Apollo), so a
    // direct cast is accurate.
    return node.totals as RecipeRunTotalsFragment | null;
  }
  return undefined;
};

/**
 * Type for queries that include visualizations count in the Finished state.
 */
type QueryWithVisualizationsCount = QueryWithChangesets;

/**
 * Extracts the visualizations count from query data.
 * Returns 0 if the recipe run is not finished or has no visualizations.
 */
export const extractVisualizationsCount = (
  data: QueryWithVisualizationsCount | undefined
): number => {
  const node = data?.organization?.changesets?.edges?.at(0)?.node;
  return isFinished(node) ? (node.visualizations?.count ?? 0) : 0;
};

/**
 * Number of data tables the run produced. Canceled and errored runs keep the
 * tables they got to, so every terminal state is read, not just Finished.
 */
export const extractDataTablesCount = (
  data: QueryWithChangesets | undefined
): number => {
  const node = data?.organization?.changesets?.edges?.at(0)?.node;
  if (isSearchNode(node)) {
    return node.dataTables?.count ?? 0;
  }
  return isTerminal(node) ? (node.dataTables?.count ?? 0) : 0;
};
